import { Alert, Button, Chip, Typography } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { Box, Stack } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import DataTable, { Column, RestoreColumnsState, SaveColumnsState, useTableColumnState } from "components/Table";
import { AdminPanelSettingsOutlined, ArrowDropDown, Refresh } from "@mui/icons-material";
import countries from "i18n-iso-countries";
import countriesEn from "i18n-iso-countries/langs/en.json";
import "flatpickr/dist/themes/airbnb.css";
import { ChipList } from "../../../../components/ChipList";
import { DateCondition, DateFilter, FilterButton, FilterFreeInput, FilterValueSelector } from "../Activity/filterbar";
import SearchIndexNotice from "../Services/_components/SearchIndexNotice";
import { formatDateTime, fromUnix } from "helpers/dateFormat";

countries.registerLocale(countriesEn);

interface AlertLogListState {
    count: number
    items: AlertLogListStateItem[]
}

interface AlertLogListStateItem {
    id: string
    alertId: string
    alertName: string
    activitySearchQuery: string
    notificationTypes: number[]
    emailRecipients: string[]
    slackChannels: string[]
    occurrencesCount: number
    timestamp: Date
    isTest: boolean
}

type columnsType = "timestamp" | "alertName" | "emailRecipients" | "occurrencesCount";


type SortDirection = "asc" | "desc";
type SortField = "timestamp" | "alertName" | "occurrencesCount";

interface FilterBarSortData {
    field: SortField
    order: SortDirection
}

export default function AlertLogs() {
    const notification = useNotification();
    const login = useLogin();
    const [finishedLoading, setFinishedLoading] = useState(false);

    const [takeCount, setTakeCount] = useState(25);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [skipItems, setSkipItems] = useState(0);

    const [lastError, setLastError] = useState("");

    const [showTimestampFilter, setShowTimestampfilter] = useState(false);
    const [showAlertnameFilter, setShowAlertnameFilter] = useState(false);
    const [showSlackChannelsFilter, setShowSlackChannelsFilter] = useState(false);
    const [showEmailRecipientsFilter, setShowEmailRecipientsFilter] = useState(false);
    const [showNotificationTypesFilter, setShowNotificationTypesFilter] = useState(false);

    const [notificationTypesFilter, setNotificationTypesFilter] = useState([] as string[]);
    const [alertNamesFilter, setAlertNamesFilter] = useState([] as string[]);
    const [slackChannelsFilter, setSlackChannelsFilter] = useState([] as string[]);
    const [emailRecipientsFilter, setEmailRecipientsFilter] = useState([] as string[]);
    const [timestampFilter, setTimeStampFilter] = useState(undefined as DateCondition | undefined);



    const columnsDataId = "dataGrid_alertLogTable_columns";

    const defaultColumns: Column<columnsType>[] = [
        { columnId: "timestamp", width: "20%", direction: "desc", sortOrder: 1 },
        { columnId: "alertName", width: "35%" },
        { columnId: "emailRecipients", width: "30%" },
        { columnId: "occurrencesCount", width: "15%" },
    ];

    const [columnsState, setColumnsState] = useTableColumnState<columnsType>(defaultColumns);

    const [listState, setListState] = useState({ items: [], count: 0 } as AlertLogListState);
	const loadGeneration = useRef(0);

    const req = login.GetAxios();

    const getFilterSorting = (c: typeof columnsState) => {
        return c.filter(x => x.sortOrder !== undefined).sort((a, b) => a.sortOrder! - b.sortOrder!).map(x => {
            return {
                field: x.columnId,
                order: x.direction!,
            } as FilterBarSortData;
        });
    };

    const getQueryFromSimple = async (sort: typeof columnsState) => {
        const r = login.GetAxios();
        if (!r) {
            return;
        }

        const x = {
            apps: [],
            clientDevices: [],
            clientIpAddresses: [],
            contentTypes: [],
            detected: [],
            detectedDatatypes: [],
            hostnames: [],
            instancesIds: [],
            location: [],
            obfuscated: [],
            obfuscatedDatatypes: [],
            originalValues: [],
            originalValueStored: [],
            rules: [],
            scanned: [],
            types: [],
            userGroups: [],
            usernames: [],
            timestamp: timestampFilter,
            alertNames: alertNamesFilter,
            slackChannels: slackChannelsFilter,
            emailRecipients: emailRecipientsFilter,
            notificationTypes: notificationTypesFilter,
        };

        const conversion = await r.post("/api/activities/convertsearch", {
            simpleToAdvanced: {
                ...x,
                orderBy: getFilterSorting(sort),
            }
        });

        return conversion.data.simpleToAdvanced || "";
    };

        const loadData = async (colState: typeof columnsState, query: string, itensPerPage: number, skip: number, generation = ++loadGeneration.current) => {
		const isCurrent = () => generation === loadGeneration.current;
        setFinishedLoading(false);
        setLastUpdated(new Date());
        setLastError("");

        if (!req) {
            return; //not logged in, nothing to do
        }
        try {


            const resp = await req.get("/api/alertlogs", {
                params: {
                    take: itensPerPage,
                    skip: skip,
                    search: query
                }
            });
			if (!isCurrent()) return;

            setListState({
                count: resp.data.count,
                items: (resp.data.items || []).map((x: any) => {
                    const item: AlertLogListStateItem = {
                        ...x,
                        timestamp: fromUnix(x.timestamp),
                    };
                    return item;
                })
            });

            const sortBy = resp.data.orderBy as FilterBarSortData[];
            const newState = colState.map(x => {
                const idx = sortBy.findIndex(y => y.field === x.columnId);
                if (idx === -1) {
                    return {
                        ...x,
                        sortOrder: undefined,
                        direction: undefined,
                    };
                }

                return {
                    ...x,
                    sortOrder: idx + 1,
                    direction: sortBy[idx].order
                };
            });

            //if the column we are sorting by is not present, we must add it
            sortBy.forEach((x, idx) => {
                const found = newState.find(y => y.columnId === x.field);
                if (!found) {
                    newState.push({
                        columnId: x.field,
                        sortOrder: idx + 1,
                        direction: x.order,
                    });
                }
            });


            setColumnsState(newState);
        } catch (err) {
			if (!isCurrent()) return;
            if (axios.isAxiosError(err) && err.response && !IsUnauthorizedError(err)) {
                setLastError(err.response.data.message);
            }
            console.error(err);
            notification.Display({
                title: "Error",
                message: "Could not fetch data",
                type: "error",
            });
            setListState({
                count: 0,
                items: [],
            });
        }
        if (isCurrent()) setFinishedLoading(true);
    };


    const firstLoad = async () => {
        const generation = ++loadGeneration.current;
        const col = RestoreColumnsState("alertLog", defaultColumns);
        const query = await getQueryFromSimple(col);
        if (generation !== loadGeneration.current) return;
        await loadData(col, query, takeCount, skipItems, generation);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const refresh = async () => {
        const generation = ++loadGeneration.current;
        const query = await getQueryFromSimple(columnsState);
        if (generation !== loadGeneration.current) return;
        loadData(columnsState, query, takeCount, skipItems, generation);
    };

    useEffect(() => {
        refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timestampFilter, alertNamesFilter, slackChannelsFilter, emailRecipientsFilter, notificationTypesFilter]);

    useEffect(() => {
        firstLoad();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        SaveColumnsState("alertLog", columnsState);
    }, [columnsState]);


    return <>
        <Box id="alertLog-page" display="flex" justifyContent="space-between">
            <Box >
                <Typography variant="h1" display="block">Alert Log</Typography>
                <Typography variant="body2" color="text.secondary">
                    Last updated at {formatDateTime(lastUpdated)} <Button size="small" variant="text" onClick={refresh}><Refresh sx={{ fontSize: "12px", marginRight: "2px" }} />refresh</Button>
                </Typography>
            </Box>
        </Box>
        <SearchIndexNotice
            stream="alert_log"
            onIndexChange={refresh}
        />
        {
            lastError && <Alert severity="error" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px", whiteSpace: "pre-line" }}>{lastError}</Alert>
        }
        {
            lastError && <Alert severity="warning" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}>New alert log records are being saved locally and will sync automatically once the connection is restored.</Alert>
        }


        <Stack direction={"row"} gap={1} marginBottom={"10px"} flexWrap={"wrap"}>
            <FilterButton
                name={"Timestamp"}
                disableClickAway={true}
                open={showTimestampFilter}
                onClick={() => setShowTimestampfilter(true)}
                onMenuClose={() => setShowTimestampfilter(false)}
                active={timestampFilter !== undefined}
                endIcon={<ArrowDropDown fontSize="small" />}
            >
                <DateFilter state={timestampFilter || {}} stateChanged={async (s) => {
                    setTimeStampFilter(s);
                }} />
            </FilterButton>
            <FilterButton
                name={"Alert Name"}
                open={showAlertnameFilter}
                onClick={() => {
                    setShowTimestampfilter(false);
                    setShowAlertnameFilter(true);
                }}
                onMenuClose={() => setShowAlertnameFilter(false)}
                active={alertNamesFilter.length > 0}
                endIcon={<ArrowDropDown fontSize="small" />}
            >
                <FilterFreeInput
                    items={alertNamesFilter}
                    inputPlaceholder="Search Alert Name"
                    itemAdded={(x) => {
                        setAlertNamesFilter(alertNamesFilter.concat(x));
                        return undefined;
                    }}
                    clearItems={() => {
                        setAlertNamesFilter([]);
                    }}
                    itemDeleted={(x) => {
                        setAlertNamesFilter(alertNamesFilter.filter(y => y !== x));
                    }}
                />
            </FilterButton>
            <FilterButton
                name={"Notification Types"}
                open={showNotificationTypesFilter}
                onClick={() => {
                    setShowTimestampfilter(false);
                    setShowNotificationTypesFilter(true);
                }}
                onMenuClose={() => setShowNotificationTypesFilter(false)}
                active={notificationTypesFilter.length > 0}
                endIcon={<ArrowDropDown fontSize="small" />}
            >
                <FilterValueSelector
                    displayShowDeleted={false}
                    enableClearButton={true}
                    items={[
                        { deleted: false, id: "1", label: "Email" },
                        { deleted: false, id: "2", label: "Slack" },
                        { deleted: false, id: "3", label: "Webhook" },
                        { deleted: false, id: "4", label: "Teams" },
                    ]}
                    selected={notificationTypesFilter}
                    stateChanged={(x) => {
                        setNotificationTypesFilter(x);
                    }}
                />
            </FilterButton>
            <FilterButton
                name={"Email Recipients"}
                open={showEmailRecipientsFilter}
                onClick={() => {
                    setShowTimestampfilter(false);
                    setShowEmailRecipientsFilter(true);
                }}
                onMenuClose={() => setShowEmailRecipientsFilter(false)}
                active={emailRecipientsFilter.length > 0}
                endIcon={<ArrowDropDown fontSize="small" />}
            >
                <FilterFreeInput
                    items={emailRecipientsFilter}
                    inputPlaceholder="Search Email Recipients"
                    itemAdded={(x) => {
                        setEmailRecipientsFilter(emailRecipientsFilter.concat(x));
                        return undefined;
                    }}
                    clearItems={() => {
                        setEmailRecipientsFilter([]);
                    }}
                    itemDeleted={(x) => {
                        setEmailRecipientsFilter(emailRecipientsFilter.filter(y => y !== x));
                    }}
                />
            </FilterButton>
            <FilterButton
                name={"Slack Channels"}
                open={showSlackChannelsFilter}
                onClick={() => {
                    setShowTimestampfilter(false);
                    setShowSlackChannelsFilter(true);
                }}
                onMenuClose={() => setShowSlackChannelsFilter(false)}
                active={slackChannelsFilter.length > 0}
                endIcon={<ArrowDropDown fontSize="small" />}
            >
                <FilterFreeInput
                    items={slackChannelsFilter}
                    inputPlaceholder="Search Notification Value"
                    itemAdded={(x) => {
                        setSlackChannelsFilter(slackChannelsFilter.concat(x));
                        return undefined;
                    }}
                    clearItems={() => {
                        setSlackChannelsFilter([]);
                    }}
                    itemDeleted={(x) => {
                        setSlackChannelsFilter(slackChannelsFilter.filter(y => y !== x));
                    }}
                />
            </FilterButton>
            <Box flexGrow={1} />
            <Button variant="outlined" onClick={() => {
                setSlackChannelsFilter([]);
                setTimeStampFilter(undefined);
                setAlertNamesFilter([]);
                setEmailRecipientsFilter([]);
                setNotificationTypesFilter([]);
            }}>Clear Filters</Button>
        </Stack>
        <DataTable
            sx={{
                maxHeight: "90%",
            }}
            size={"medium"}
            dataCount={listState.count}
            paginationStartItem={skipItems}
            paginationItemCount={takeCount}
            onPaginationStartItemChanged={async (x) => {
                setSkipItems(x);
                setTakeCount(takeCount);
                // Claim the generation before the conversion, not after: two
                // quick page changes whose conversions resolve out of order
                // would otherwise leave the older one current, and the rows
                // would not be the page skipItems now says we are on.
                const generation = ++loadGeneration.current;
                const query = await getQueryFromSimple(columnsState);
                if (generation !== loadGeneration.current) return;
                loadData(columnsState, query, takeCount, x, generation);
            }}
            onPaginationItemCountChanged={async (x) => {
                setTakeCount(Number(x));
                const generation = ++loadGeneration.current;
                const query = await getQueryFromSimple(columnsState);
                if (generation !== loadGeneration.current) return;
                loadData(columnsState, query, Number(x), skipItems, generation);
            }}
            onSortChanged={async (c) => {
                const r = login.GetAxios();
                if (!r) {
                    return;
                }

                const generation = ++loadGeneration.current;
                const query = await getQueryFromSimple(columnsState);
                if (generation !== loadGeneration.current) return;

                const conversion = await r.post("/api/activities/convertsearch", {
                    modifyAdvanced: {
                        query: query,
                        orderBy: getFilterSorting(c),
                    }
                });
                if (generation !== loadGeneration.current) return;

                const response = conversion.data.modifyAdvanced || "";

                loadData(columnsState, response, takeCount, skipItems, generation);
            }}
            data={listState.items}
            columnsState={columnsState}
            finishedLoading={finishedLoading}
            onColumnOrderChanged={(x) => setColumnsState(x)}
            columnsDataId={columnsDataId}
            columns={{
                timestamp: {
                    displayName: "Timestamp",
                    sortable: true,
                    render: (x) => {
                        return <Typography fontSize="small">{formatDateTime(x.timestamp)}</Typography>;
                    },
                },
                alertName: {
                    displayName: "Alert Name",
                    sortable: true,
                    render: (x) => {
                        return x.alertName;
                    }
                },
                emailRecipients: {
                    displayName: "Notifications",
                    render: (x) => {
                        return <ChipWrap>
                            <ChipList items={x.emailRecipients} renderItem={(x) => <Chip label={x} title={x} variant="outlined" size="small" color="primary" />} />
                            <ChipList items={x.slackChannels} renderItem={(x) => <Chip label={x} title={x} variant="outlined" size="small" color="success" />} />
                            {x.notificationTypes && x.notificationTypes.includes(3) && <Chip label={"Webhook"} title={"Webhook"} variant="outlined" size="small" color="warning" />}
                            {x.notificationTypes && x.notificationTypes.includes(4) && <Chip label={"Teams"} title={"Teams"} variant="outlined" size="small" color="secondary" />}
                        </ChipWrap>;

                    },
                },
                occurrencesCount: {
                    sortable: true,
                    displayName: "Occurrences Count",
                    render: (x) => {
                        return x.occurrencesCount;
                    }
                },
            }
            }
            rowOptionsMenu={[
                {
                    icon: <AdminPanelSettingsOutlined className="show-activity-button" fontSize="small" />,
                    text: "See Data Log",
                    onClick: (x) => {
                        window.open("/activities?query=" + encodeURIComponent(x.activitySearchQuery), "_blank");
                    },
                    isVisible: (x) => { return !x.isTest; },
                }
            ]}
        />
    </>;
}